<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="status.isLoading"
    loading-message="Loading Ride History..."
    button-location="top"
    @submit="loadRides()"
  >
    <template #title>
      Ride History (Deactivated Members)
    </template>

    <template #top-form>
      <div class="row">
        <label class="col-sm-3 control-label pt-10"> Date Range </label>
        <div class="col-sm-6">
          <date-picker
            v-model="timeSpanValue"
            type="daterange"
            required
          >
            <template #comment>
              <span class="help-block"> Please click the same day twice if you only want results from that particular day </span>
            </template>
          </date-picker>
        </div>
      </div>

      <div class="form-submit-button-group">
        <p-button
          type="purple"
          round
          native-type="submit"
          :disabled="status.isLoading"
        >
          Search
        </p-button>

        <p-button
          type="info"
          round
          size="md"
          :disabled="status.isLoading"
          @click="downloadData"
        >
          Download
        </p-button>

        <p-button
          type="yellow"
          round
          :disabled="status.isLoading"
          @click="resetForm"
        >
          Reset
        </p-button>
      </div>

      <div class="row align-items-center">
        <pagination
          :is-loading="status.isLoading"
          :current-page="status.currentPage"
          :total-pages="status.totalPages"
          @load-page-data="loadRides"
        />
      </div>
    </template>

    <template #content>
      <table-view-container
        :page-id="pageId"
        :data="rides"
        :table-columns="rideHistoryTableColumnsForPartner"
      />
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, watch } from 'vue';
import Moment from 'moment-timezone';

import util from '@/Utils/util';
import rideUtility from '@/Utils/rideUtility';
import { usePartner } from '@/use/partner';
import { catchErrorMessageBox } from '@/Utils/catchErrorMessageBox';
import { rideHistoryTableColumnsForPartner } from '@/constants/ride';
import { Ride, ExtendedRide } from '@/models/ride';
import { getRidesByDeactivatedPartnerCallers } from '@/services/partners/getRidesByDeactivatedPartnerCallers';
import { downloadRidesByDeactivatedPartnerCallers } from '@/services/partners/downloadRidesByDeactivatedPartnerCallers';

const pageId = 'deactivated-callers-rides';
const paginationLimit: number = 30;

const { partnerId } = usePartner();

const rides: Ref<ExtendedRide[]> = ref([]);
const timeSpanValue: Ref<any> = ref(null);
const searchConditions = reactive({
  to: '',
  from: '',
});
const status = reactive({
  isLoading: false,
  totalPages: 1,
  currentPage: 1,
});

const loadRides = (currentPage: number = 1) => {
  const offset = (currentPage - 1) * paginationLimit;
  status.currentPage = currentPage;
  status.isLoading = true;

  return catchErrorMessageBox(async () => {
    const response = await getRidesByDeactivatedPartnerCallers({
      from: searchConditions.from,
      to: searchConditions.to,
      partnerId: partnerId.value,
      pagination: {
        offset,
        limit: paginationLimit,
      },
    });

    status.totalPages = Math.ceil(response.count / paginationLimit) || 1;

    rides.value = response.rows.map((ride: Ride, index: number) => {
      const data = rideUtility.formatRideData(ride);
      data.index = (currentPage - 1) * paginationLimit + index + 1;

      return data;
    });
  })
    .finally(() => (status.isLoading = false))
    .catch(() => {
      rides.value = [];
      status.totalPages = 1;
      status.currentPage = 1;
    });
};

const downloadData = async () => {
  catchErrorMessageBox(async () => {
    const csvData = await downloadRidesByDeactivatedPartnerCallers({
      from: searchConditions.from,
      to: searchConditions.to,
      partnerId: partnerId.value,
    });

    const strStartDate = Moment(searchConditions.from).format('YYYY-MM-DD');
    const strEndDate = Moment(searchConditions.to).format('YYYY-MM-DD');

    util.downloadJSONToCSV(csvData, `Rides_By_Deactivated_Callers (${strStartDate} ~ ${strEndDate}).csv`);
  })
    .finally(() => (status.isLoading = false))
    .catch(() => null);
};

const resetForm = () => {
  rides.value = [];
  status.totalPages = 1;
  status.currentPage = 1;

  timeSpanValue.value = null;
};

watch(timeSpanValue, (value: any) => {
  if (value) {
    searchConditions.from = util.formatDateTime(value[0], 'YYYY-MM-DD HH:mm:ss');
    searchConditions.to = util.formatDateTime(Moment(value[1]).endOf('day'), 'YYYY-MM-DD HH:mm:ss');
  } else {
    searchConditions.from = '';
    searchConditions.to = '';
  }
});
</script>
